/**
 * AdminPresence — Live-Übersicht „Wer ist gerade online?"
 *
 * Zugriff: admin + super_admin (Backend-Scoped auf Tenant, super_admin sieht alles).
 * Auto-Refresh alle 30 s.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Activity, RefreshCw, Users2, Clock, Eye, Loader2,
} from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import api from "@/lib/api";

const PRESENCE_COLOR = {
  online:  "bg-[#00E575]",
  recent:  "bg-amber-400",
  offline: "bg-zinc-600",
};
const PRESENCE_LABEL = {
  online:  "online",
  recent:  "kürzlich",
  offline: "offline",
};

const fmtMin = (m) => {
  if (!m) return "—";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}min` : `${h}h`;
};

const fmtLastSeen = (iso, minutes) => {
  if (!iso) return "—";
  if (minutes != null && minutes < 1) return "gerade eben";
  if (minutes != null && minutes < 60) return `vor ${Math.round(minutes)} min`;
  if (minutes != null && minutes < 60 * 24) return `vor ${Math.round(minutes/60)} h`;
  return new Date(iso).toLocaleString("de-DE");
};

const shortRoute = (r) => {
  if (!r) return "—";
  return r.replace(/^\/api/, "").replace(/^\//, "") || "/";
};


export default function AdminPresence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/admin/presence");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { if (!silent) setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, []);

  const users = data?.users || [];
  const hourly = data?.hourly_24h || [];
  const filtered = useMemo(() => {
    let arr = users;
    if (filter !== "all") arr = arr.filter((u) => u.presence === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((u) => [u.name, u.email, u.role]
        .filter(Boolean).some((s) => String(s).toLowerCase().includes(q)));
    }
    return arr;
  }, [users, filter, search]);

  const maxBar = Math.max(1, ...hourly.map((h) => h.active_users || 0));

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-presence">
      <AppHeader title="Live-Übersicht" back="/admin/users" />
      <main className="max-w-6xl mx-auto px-3 sm:px-5 py-5 sm:py-7">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-2xl bg-[#00E575]/15 flex items-center justify-center">
            <Activity className="text-[#00E575]" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Wer ist gerade online?</h1>
            <p className="text-zinc-400 text-[12px] mt-0.5 font-medium">
              Live-Übersicht aller Benutzer · Auto-Refresh alle 30 s
            </p>
          </div>
          <button
            onClick={() => load()}
            className="shrink-0 h-10 w-10 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 flex items-center justify-center"
            data-testid="presence-refresh"
            title="Manuell aktualisieren"
          >
            <RefreshCw size={14} className={loading ? "animate-spin text-[#00E575]" : "text-zinc-400"} />
          </button>
        </div>

        {/* KPI Stats */}
        <div className="grid grid-cols-3 gap-2.5 mb-5">
          <StatCard icon={Activity} label="Online jetzt"
            value={data?.summary?.online_now ?? "…"} accent="text-[#00E575]" testid="stat-online" />
          <StatCard icon={Clock} label="Aktiv heute"
            value={data?.summary?.active_today ?? "…"} accent="text-amber-400" testid="stat-today" />
          <StatCard icon={Users2} label="Benutzer gesamt"
            value={data?.summary?.total_users ?? "…"} accent="text-sky-400" testid="stat-total" />
        </div>

        {/* 24h histogram */}
        {hourly.length > 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 mb-5">
            <h2 className="font-bold text-[12px] uppercase tracking-widest text-zinc-200 mb-3">
              Aktive Benutzer (letzte 24 h)
            </h2>
            <div className="flex items-end gap-0.5 h-20">
              {hourly.map((h, i) => (
                <div
                  key={i}
                  className="flex-1 bg-[#00E575]/30 hover:bg-[#00E575]/60 rounded-t transition-colors min-w-[6px]"
                  style={{ height: `${Math.max(4, (h.active_users / maxBar) * 100)}%` }}
                  title={`${h.hour}: ${h.active_users} aktive`}
                />
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[12px] text-zinc-300">
              <span>vor 24 h</span>
              <span>jetzt</span>
            </div>
          </div>
        )}

        {/* Filter + Search */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input
            type="text"
            placeholder="Suche: Name, E-Mail, Rolle…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="presence-search"
            className="h-10 flex-1 min-w-[200px] bg-[#09090B] border border-zinc-800 rounded-lg px-3 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none"
          />
          {["all", "online", "recent", "offline"].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`presence-filter-${k}`}
              className={`h-10 px-3 rounded-lg text-[12px] font-bold border ${
                filter === k
                  ? "bg-[#00E575] text-[#09090B] border-[#00E575]"
                  : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:bg-zinc-800"
              }`}
            >
              {k === "all" ? "Alle" : PRESENCE_LABEL[k]}
            </button>
          ))}
        </div>

        {/* User list */}
        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin inline text-zinc-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-10 text-center">
            <p className="text-zinc-400 text-[13px]">Keine Benutzer mit diesem Filter.</p>
          </div>
        ) : (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
            <table className="w-full text-[12.5px]" data-testid="presence-table">
              <thead className="bg-[#0c0c0e] text-[12px] uppercase tracking-widest text-zinc-300 font-semibold">
                <tr>
                  <th className="py-2.5 px-3 text-left">Status</th>
                  <th className="py-2.5 px-3 text-left">Name</th>
                  <th className="py-2.5 px-3 text-left">Rolle</th>
                  <th className="py-2.5 px-3 text-left hidden sm:table-cell">Letzter Aufruf</th>
                  <th className="py-2.5 px-3 text-right">Heute</th>
                  <th className="py-2.5 px-3 text-right hidden sm:table-cell">7 Tage</th>
                  <th className="py-2.5 px-3 text-right hidden md:table-cell">Letzte Aktivität</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr
                    key={u.id}
                    data-testid={`presence-row-${u.id}`}
                    className="border-t border-zinc-800 hover:bg-zinc-900/40"
                  >
                    <td className="py-2.5 px-3">
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`h-2.5 w-2.5 rounded-full ${PRESENCE_COLOR[u.presence]} ${u.presence === "online" ? "animate-pulse" : ""}`} />
                        <span className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold">{PRESENCE_LABEL[u.presence]}</span>
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-bold text-zinc-100">{u.name}</div>
                      <div className="text-[10.5px] text-zinc-500 truncate max-w-[200px]">{u.email}</div>
                    </td>
                    <td className="py-2.5 px-3 text-zinc-400">{u.role}</td>
                    <td className="py-2.5 px-3 hidden sm:table-cell">
                      <span className="font-mono text-[12px] text-zinc-400 inline-flex items-center gap-1">
                        <Eye size={10} /> {shortRoute(u.last_route)}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-bold text-zinc-100">{fmtMin(u.today_minutes)}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-zinc-400 hidden sm:table-cell">{fmtMin(u.week_minutes)}</td>
                    <td className="py-2.5 px-3 text-right text-zinc-500 hidden md:table-cell">
                      {fmtLastSeen(u.last_seen_at, u.minutes_since)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}


function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4">
      <Icon className={accent} size={18} />
      <div className="font-display text-3xl font-bold mt-2 leading-none">{value}</div>
      <div className="text-[12px] text-zinc-400 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}
